import React, { useMemo } from 'react';
import moment from 'moment';

// MUI / MRT
import { ThemeProvider } from '@mui/material/styles';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';

import { useActionLock } from '../../../../../../hooks/useActionLock';

// icons
import { Edit, Delete, ReceiptLong } from '@mui/icons-material';

// ✅ ajuste os paths abaixo conforme seu projeto
import Select from '../../../../../form/Select';
import Input from '../../../../../form/Input';
import InputNumber from '../../../../../form/InputNumber';

import { retornaData } from '../../../../../../utils/retornaDataFormatada';
import { formatarNumero } from '../../../../../../utils/masks';

export default function AditivoTabPanel({
    // UI/Tema
    theme,
    // môdule headless
    module,

    // contexto ainda externo (por enquanto)
    operacao,
    operacaoParametrosDocumentos,

    // enums / options
    enumTipoAditivo,

    // permissões
    podeImprimir,
    podeEditarItem,
    podeCancelarItem,

    //funções
    navigate,
}) {
    const { main, draft, list, mrt, flags, actions } = module || {};

    const dados = main?.state || {};
    const novoAditivo = draft?.value || {};

    const data = list?.items || [];
    const rowCount = list?.rowCount || 0;

    const {
        change: onChange = () => {},
        focus: onFocus = () => {},
        blur: onBlur = () => {},
        clear: onLimparAditivo = () => {},
        submitDraft: onSalvarAditivo = async () => {},
        selectRow: onEditarAditivoRow = () => {},
        remove: onDeleteAditivoRow = async () => {},
        print: onImprimirDocumento = async () => {},
    } = actions || {};

    const { isSaving = false } = flags || {};

    const {
        state: {
            columnFilterFns = {},
            columnFilters = [],
            globalFilter = '',
            pagination = { pageIndex: 0, pageSize: 10 },
            sorting = [],
        } = {},
        setters: {
            setColumnFilterFnsMode = () => {},
            setColumnFilters = () => {},
            setGlobalFilter = () => {},
            setPagination = () => {},
            setSorting = () => {},
        } = {},
        flags: { isLoading = false, isRefetching = false, isError = false } = {},
    } = mrt || {};

    const isProrrogar = (novoAditivo?.tipo || '')?.toLowerCase() === 'prorrogar';
    const isReajuste = (novoAditivo?.tipo || '')?.toLowerCase() === 'reajuste';

    const columns = useMemo(
        () => [
            {
                id: 'aditivo',
                header: 'Aditivos do contrato',
                columns: [
                    { accessorKey: 'tipo', id: 'tipo', header: 'Tipo' },
                    {
                        accessorFn: (row) => new Date(row),
                        accessorKey: 'dataAditivo',
                        header: 'Data',
                        Header: ({ column }) => <em>{column.columnDef.header}</em>,
                        muiFilterTextFieldProps: { sx: { minWidth: '200px' } },
                        filterVariant: 'date',
                        sortingFn: 'date',
                        columnFilterModeOptions: [
                            'fuzzy',
                            'equals',
                            'between',
                            'betweenInclusive',
                            'greaterThanOrEqualTo',
                            'lessThanOrEqualTo',
                        ],
                        muiTableBodyCellProps: { align: 'center' },
                        muiTableHeadCellFilterTextFieldProps: {
                            type: 'date',
                            InputLabelProps: { shrink: false },
                            sx: { minWidth: '200px' },
                        },
                        Cell: ({ row }) => (
                            <span>
                                {row.original.dataAditivo
                                    ? moment.utc(row.original.dataAditivo).startOf('day').format('DD/MM/YYYY')
                                    : null}
                            </span>
                        ),
                    },
                    {
                        accessorKey: 'valorAnterior',
                        header: 'Valor Anterior',
                        muiTableBodyCellProps: { align: 'right' },
                        accessorFn: (row) =>
                            row.valorAnterior === null
                                ? ''
                                : 'R$ ' +
                                  parseFloat(row.valorAnterior)?.toLocaleString('pt-BR', {
                                      style: 'decimal',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                      minimumIntegerDigits: 1,
                                      useGrouping: true,
                                  }),
                    },
                    {
                        accessorKey: 'percentual',
                        header: 'Percentual',
                        muiTableBodyCellProps: { align: 'right' },
                        accessorFn: (row) =>
                            row.percentual === null
                                ? ''
                                : parseFloat(row.percentual)?.toLocaleString('pt-BR', {
                                      style: 'decimal',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                      minimumIntegerDigits: 1,
                                      useGrouping: true,
                                  }) + ' %',
                    },
                    {
                        accessorKey: 'valorReajustado',
                        header: 'Aluguel',
                        muiTableBodyCellProps: { align: 'right' },
                        accessorFn: (row) =>
                            row.valorReajustado === null
                                ? ''
                                : 'R$ ' +
                                  parseFloat(row.valorReajustado)?.toLocaleString('pt-BR', {
                                      style: 'decimal',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                      minimumIntegerDigits: 1,
                                      useGrouping: true,
                                  }),
                    },
                    {
                        accessorKey: 'quantidade',
                        header: 'Meses',
                        muiTableBodyCellProps: { align: 'center' },
                        accessorFn: (row) => (row.quantidade === null ? '' : Math.floor(Number(row.quantidade))),
                    },
                ],
            },
        ],
        []
    );

    return (
        <div style={{ margin: '1em' }}>
            {/* ====== FORM DRAFT ====== */}
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap' }}>
                <Select
                    name="novoAditivo.tipo"
                    title="escolha o tipo de termo aditivo ao contrato"
                    text="Tipo"
                    inputWidth="150px"
                    options={enumTipoAditivo}
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={novoAditivo?.tipo}
                    isEnum={true}
                    notOptNull={false}
                />

                <Input
                    text="Data"
                    type="date"
                    name="novoAditivo.dataAditivo"
                    placeholder="Data"
                    title="Data do termo aditivo"
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={novoAditivo?.dataAditivo ? moment(novoAditivo.dataAditivo).format('YYYY-MM-DD') : ''}
                />

                <InputNumber
                    text="Valor Atual"
                    type="text"
                    name="novoAditivo.valorAnterior"
                    inputWidth="100px"
                    title="Valor atual do aluguel"
                    readonly={true}
                    value={
                        novoAditivo?.valorAnterior
                            ? formatarNumero
                                ? formatarNumero(novoAditivo.valorAnterior)
                                : novoAditivo.valorAnterior
                            : null
                    }
                />

                <InputNumber
                    text="Reajuste"
                    type="text"
                    name="novoAditivo.percentual"
                    placeholder="Valor em %"
                    inputWidth="100px"
                    title="digite o percentual de reajuste do aluguel"
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={
                        novoAditivo?.percentual
                            ? formatarNumero
                                ? formatarNumero(novoAditivo.percentual)
                                : novoAditivo.percentual
                            : ''
                    }
                />

                <InputNumber
                    text="Aluguel"
                    type="text"
                    name="novoAditivo.valorReajustado"
                    placeholder="Valor R$"
                    inputWidth="100px"
                    title="digite o valor do aluguel"
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={
                        novoAditivo?.valorReajustado
                            ? formatarNumero
                                ? formatarNumero(novoAditivo.valorReajustado)
                                : novoAditivo.valorReajustado
                            : ''
                    }
                />

                <InputNumber
                    text="Meses"
                    type="text"
                    name="novoAditivo.quantidade"
                    placeholder="Período em meses"
                    inputWidth="100px"
                    title="digite a quantidade de meses da prorrogação"
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={
                        novoAditivo?.quantidade
                            ? formatarNumero
                                ? formatarNumero(novoAditivo.quantidade)
                                : novoAditivo.quantidade
                            : ''
                    }
                    readonly={!isProrrogar} // ✅ meses só no prorrogar
                />

                <Button
                    variant="contained"
                    color="inherit"
                    onClick={onLimparAditivo}
                    style={{ marginLeft: '10px', minWidth: '80px', fontSize: '14px' }}
                >
                    Limpar
                </Button>

                <Button
                    variant="contained"
                    color={(novoAditivo?.funcao || '')?.toLowerCase() === 'deletar' ? 'error' : 'info'}
                    onClick={() => onSalvarAditivo && onSalvarAditivo(novoAditivo)}
                    disabled={!novoAditivo?.tipo}
                    style={{ marginLeft: '10px', minWidth: '80px', fontSize: '14px' }}
                >
                    {novoAditivo?.funcao || 'Adicionar'}
                </Button>
            </div>

            {/* ====== GRID ====== */}
            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    displayColumnDefOptions={{
                        'mrt-row-actions': { muiTableHeadCellProps: { align: 'center' }, size: 200 },
                        'mrt-row-numbers': {
                            enableColumnOrdering: true,
                            enableResizing: true,
                            muiTableHeadCellProps: { sx: { fontSize: '1.2rem' } },
                        },
                        'mrt-row-select': { enableColumnActions: true, enableHiding: true, size: 50 },
                    }}
                    enableColumnFilterModes
                    enableGlobalFilterModes
                    enableColumnOrdering
                    enableEditing
                    renderRowActions={({ row }) => {
                        const rowIndex = row.index;
                        const rowOriginal = row.original;

                        return (
                            <Box sx={{ display: 'flex', gap: '1rem' }}>
                                <Tooltip arrow placement="left" title="Editar">
                                    <span>
                                        <IconButton
                                            onClick={() => onEditarAditivoRow && onEditarAditivoRow(rowOriginal, rowIndex)}
                                            disabled={!podeEditarItem}
                                        >
                                            <Edit />
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                <Tooltip arrow placement="top" title="Deletar">
                                    <span>
                                        <IconButton
                                            color="error"
                                            onClick={() => onDeleteAditivoRow && onDeleteAditivoRow(rowOriginal, rowIndex)}
                                            disabled={!podeCancelarItem}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                {operacaoParametrosDocumentos.map((documento, index) => (
                                    <Tooltip
                                        key={index}
                                        arrow
                                        placement="top-start"
                                        title={`Imprimir ${documento?.Documento?.tipo}`}
                                    >
                                        <span>
                                            <IconButton
                                                onClick={() => onImprimirDocumento && onImprimirDocumento(documento, rowOriginal)}
                                                disabled={!podeImprimir}
                                            >
                                                {documento?.Documento?.tipo === 'aditivo' && <ReceiptLong />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                ))}
                            </Box>
                        );
                    }}
                    renderTopToolbarCustomActions={() => (
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Tooltip arrow placement="top-end" title="Novo Registro">
                                <div>
                                    <Button
                                        color="secondary"
                                        onClick={() => navigate('/TabelaPrecos/CreateOrEdit')}
                                        variant="contained"
                                        disabled
                                    >
                                        Novo
                                    </Button>
                                </div>
                            </Tooltip>
                        </div>
                    )}
                    columns={columns}
                    data={data || []}
                    enableRowSelection
                    getRowId={(row) => row.uuid}
                    manualFiltering
                    manualPagination
                    manualSorting
                    muiToolbarAlertBannerProps={isError ? { color: 'error', children: 'Error loading data' } : undefined}
                    onColumnFiltersChange={setColumnFilters}
                    onColumnFilterFnsChange={setColumnFilterFnsMode}
                    onGlobalFilterChange={setGlobalFilter}
                    onPaginationChange={setPagination}
                    onSortingChange={setSorting}
                    rowCount={rowCount}
                    initialState={{ showColumnFilters: true, density: 'compact' }}
                    state={{
                        columnFilterFns,
                        columnFilters,
                        globalFilter,
                        isLoading,
                        pagination,
                        showAlertBanner: isError,
                        showProgressBars: isRefetching,
                        sorting,
                    }}
                    positionExpandColumn="first"
                    positionActionsColumn="first"
                    positionToolbarAlertBanner="bottom"
                    localization={MRT_Localization_PT_BR}
                />
            </ThemeProvider>
        </div>
    );
}
